// src/components/CruiseSearchTabs.jsx
import React, { useState, useMemo, useCallback } from "react";
import { Tabs, Select, InputNumber, Button, Tag, Flex } from "antd";
import {
  CloseOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";

import CruiseMonthSelector from "./CruiseMonthSelector";
import DeparturePortFilter from "./DeparturePortFilter";

import { ReactComponent as TicketOutline } from "../assets/images/menu-ticket.svg";
import { ReactComponent as TourOutline } from "../assets/images/menu-tour.svg";
import { ReactComponent as TravelService } from "../assets/images/menu-travel-services.svg";
import { ReactComponent as CruiseOutline } from "../assets/images/menu-cruise.svg";
import { ReactComponent as AdventureIcon } from "../assets/images/menu-adventure.svg";
import NumberOfNightsFilter from "./NumberOfNightsFilter";
import NumberOfGuestsFilter from "./NumberOfGuestsFilter";

const { TabPane } = Tabs;

const CruiseSearchTabs = () => {
  const [filters, setFilters] = useState([]);
  const [activeKey, setActiveKey] = useState("dates"); // 👈 Track which tab is open

  // Memoized list of selected ports for DeparturePortFilter
  const selectedPorts = useMemo(
    () => filters.filter((f) => f.key.startsWith("port-")).map((f) => f.label),
    [filters]
  );

  // Memoized handler to update date/month filters from CruiseMonthSelector
  const handleSelectFilters = useCallback((selected) => {
    setFilters((prev) => {
      // Remove all existing date/month filters
      const rest = prev.filter(
        (f) => !f.key.startsWith("date") && !f.key.startsWith("month")
      );
      return [...rest, ...selected];
    });
  }, []);

  // Update or add filters by key
  const updateFilter = useCallback((key, labelOrLabels) => {
    setFilters((prev) => {
      // Remove any filters with this key or starting with this key-
      const filtered = prev.filter(
        (f) => !(f.key === key || f.key.startsWith(`${key}-`))
      );

      // Normalize input to array of { key, label }
      const incoming = Array.isArray(labelOrLabels)
        ? labelOrLabels.map((label) => ({ key: `${key}-${label}`, label }))
        : [{ key: `${key}-${labelOrLabels}`, label: labelOrLabels }];

      const updated = [...filtered, ...incoming];

      // Avoid update if no change (to prevent unnecessary re-renders or loops)
      const isSame =
        prev.length === updated.length &&
        prev.every(
          (item, idx) =>
            item.key === updated[idx].key && item.label === updated[idx].label
        );

      if (isSame) return prev;

      
      return updated;
    });
  }, []);

  // Remove filter by exact key or keys starting with the key prefix
  const removeFilter = useCallback((key) => {
    setFilters((prev) =>
      prev.filter((f) => !(f.key === key || f.key.startsWith(`${key}-`)))
    );
  }, []);

  const resetAll = useCallback(() => {
    setFilters([]);
  }, []);

  const handleApply = useCallback(() => {    
    setActiveKey(null); 
  }, [filters]);

  return (
    <div className="container">
      <div className="bg-white rounded-2xl shadow-lg">
        <Tabs
          defaultActiveKey="dates"
          tabBarGutter={32}
          tabPosition="top"
          tabBarStyle={{ marginBottom: 24 }}
          activeKey={activeKey}
          onChange={(key) => setActiveKey(key)}
          className="mainMenu activeColor 
                [&_.ant-tabs-tab]:!px-5 
                [&_.ant-tabs-tab]:!py-6 
                [&_.ant-tabs-tab]:!text-gray-700
                [&_.ant-tabs-tab:hover]:!bg-primary 
                [&_.ant-tabs-tab:hover]:!text-white 
                [&_.ant-tabs-tab:hover_svg]:!text-white
                [&_.ant-tabs-tab-active]:!bg-primary 
                [&_.ant-tabs-tab-active]:!text-white 
                [&_.ant-tabs-tab-active_svg]:!text-white"
        >
          {/* Cruise Dates Tab */}
          <TabPane
            tab={
              <Flex align="center" gap={8}>
                <TicketOutline />
                Cruise Dates
              </Flex>
            }
            key="dates"
            className="px-6 pb-6"
          >
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Select Cruise Date
            </h3>
            <CruiseMonthSelector
              selectedFilters={filters.filter(
                (f) => f.key.startsWith("date") || f.key.startsWith("month")
              )}
              onSelectFilters={handleSelectFilters}
            />
          </TabPane>

          {/* Departure Port */}
          <TabPane
            tab={
              <Flex align="center" gap={8}>
                <TourOutline />
                Departure Port
              </Flex>
            }
            key="ports"
            className="px-6 pb-6"
          >
            <DeparturePortFilter
              value={selectedPorts}
              onChange={(ports) => updateFilter("port", ports)}
            />
          </TabPane>

          {/* Nights */}
          <TabPane
            tab={
              <Flex align="center" gap={8}>
                <AdventureIcon />
                Number of Nights
              </Flex>
            }
            key="nights"
            className="px-6 pb-6"
          >
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Select Number of Nights
            </h3>
            <NumberOfNightsFilter
              value={filters
                .filter((f) => f.key.startsWith("nights-"))
                .map((f) => f.label)}
              onChange={(selected) => updateFilter("nights", selected)}
            />
          </TabPane>

          {/* Cruise Line */}
          <TabPane
            tab={
              <Flex align="center" gap={8}>
                <CruiseOutline />
                Number of Guest
              </Flex>
            }
            key="cruises"
            className="px-6 pb-6"
          >
            <NumberOfGuestsFilter
              value={
                filters.find((f) => f.key === "guests")?.value || {
                  adults: 2,
                  children: 0,
                }
              }
              onChange={(updated) =>
                updateFilter(
                  "guests",
                  `${updated.adults} Adults, ${updated.children} Children`
                )
              }
            />
          </TabPane>
        </Tabs>

        {/* ALL SELECTED FILTERS BELOW TABS */}
        {filters.length > 0 && (
          <div className="border-t px-6 pt-4 pb-6">
            <p className="text-sm text-gray-600 font-medium mb-2">
              All Filters Selected
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
              {filters.map((filter) => (
                <Tag
                  key={filter.key}
                  closable
                  closeIcon={<CloseOutlined />}
                  onClose={() => removeFilter(filter.key)}
                  className="px-3 py-1 text-sm bg-blue-50 border-blue-300 text-blue-600 rounded-full"
                >
                  {filter.label}
                </Tag>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <Button icon={<DeleteOutlined />} onClick={resetAll}>
                Reset All
              </Button>
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={handleApply}
              >
                Apply Filters
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CruiseSearchTabs;
